
import { useRef, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

function useReveal(delay = 0) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    // Guard against environments where IntersectionObserver is not available
    if (typeof IntersectionObserver === 'undefined') {
      // Fallback: make the element visible immediately
      setVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.12, rootMargin: '0px 0px -40px 0px' }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return { ref, visible, delay };
}

function FeatureCard({
  feature,
  index,
}: {
  feature: { icon: string; title: string; description: string };
  index: number;
}) {
  const { ref, visible } = useReveal();
  return (
    <div
      ref={ref}
      style={{ transitionDelay: `${index * 120}ms` }}
      className={`flex items-start gap-3 bg-[#4A7C23]/70 backdrop-blur-sm rounded-xl p-3 sm:p-5 transition-all duration-700
        ${visible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'}`}
    >
      <div className="w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center bg-white/20 rounded-full flex-shrink-0 mt-0.5">
        <i className={`${feature.icon} text-base sm:text-xl text-white`}></i>
      </div>
      <div className="min-w-0">
        <h3 className="text-white font-semibold text-[0.8rem] sm:text-base mb-0.5 sm:mb-1">{feature.title}</h3>
        <p className="text-white/80 text-[0.7rem] sm:text-sm leading-relaxed">{feature.description}</p>
      </div>
    </div>
  );
}

function StatCard({
  stat,
  index,
}: {
  stat: { value?: string; icon?: string; label: string };
  index: number;
}) {
  const { ref, visible } = useReveal();
  return (
    <div
      ref={ref}
      style={{ transitionDelay: `${index * 120}ms` }}
      className={`bg-[#4A7C23]/70 backdrop-blur-sm rounded-xl p-3 sm:p-6 flex flex-col items-center justify-center text-center transition-all duration-700
        ${visible ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-6 scale-95'}`}
    >
      {'value' in stat && stat.value ? (
        <span className="text-xl sm:text-4xl md:text-5xl font-bold text-white mb-1 sm:mb-2">{stat.value}</span>
      ) : (
        <div className="w-7 h-7 sm:w-12 sm:h-12 flex items-center justify-center mb-1 sm:mb-2">
          <i className={`${stat.icon} text-xl sm:text-4xl text-white`}></i>
        </div>
      )}
      <span className="text-white/90 text-[9px] sm:text-xs font-semibold tracking-wide uppercase leading-tight">
        {stat.label}
      </span>
    </div>
  );
}

export default function SocialProof() {
  const { t } = useTranslation();

  const features = [
    { icon: 'ri-global-line', title: t('social.f1.title'), description: t('social.f1.desc') },
    { icon: 'ri-leaf-line', title: t('social.f2.title'), description: t('social.f2.desc') },
    { icon: 'ri-plant-line', title: t('social.f3.title'), description: t('social.f3.desc') },
  ];

  const stats = [
    { value: t('social.s1.value'), label: t('social.s1.label') },
    { value: t('social.s2.value'), label: t('social.s2.label') },
    { value: t('social.s3.value'), label: t('social.s3.label') },
    { icon: 'ri-sun-line', label: t('social.s4.label') },
  ];

  return (
    <section id="costa-rica" className="relative py-10 sm:py-24 overflow-hidden">
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{
          backgroundImage:
            "url('https://public.readdy.ai/ai/img_res/18851f52faf4dc32c7ee2083e34bcde4.jpg')",
        }}
      />
      <div className="absolute inset-0 bg-black/40" />

      <div className="relative z-10 max-w-6xl mx-auto px-5 sm:px-6">
        <div className="mb-5 sm:mb-10">
          <span className="inline-block px-3 py-1 sm:py-1.5 bg-[#4A7C23]/80 text-white text-[10px] sm:text-xs font-semibold uppercase tracking-wider rounded-full mb-2 sm:mb-4">
            {t('social.tag')}
          </span>
          <h2 className="text-[1.4rem] leading-tight sm:text-4xl md:text-5xl font-bold text-white mb-2 sm:mb-4">
            {t('social.title')}
          </h2>
          <p className="text-white/90 max-w-2xl text-xs sm:text-base leading-relaxed">{t('social.desc')}</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-6">
          <div className="space-y-2.5 sm:space-y-3">
            {features.map((feature, index) => (
              <FeatureCard key={index} feature={feature} index={index} />
            ))}
          </div>
          <div className="grid grid-cols-2 gap-2.5 sm:gap-4">
            {stats.map((stat, index) => (
              <StatCard key={index} stat={stat} index={index} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
